import { createEffect, createMemo, createSignal, onCleanup, Show, untrack, type ParentProps } from "solid-js"
import { useLocation } from "@solidjs/router"
import {
  machineKey,
  useFleet,
  type FleetMachine,
} from "@/context/fleet"
import { ServerConnection, useServer } from "@/context/server"
import { parseMachineFromPath } from "@/utils/machine-route"
import { chooseMachine } from "./machine-choice"
import { fleetStartupReady } from "./startup"
import { purgeFleetWorkspaceState } from "@/utils/fleet-cleanup"

export function AutoMachineConnector(props: ParentProps) {
  const server = useServer()
  const fleet = useFleet()
  const location = useLocation()
  const [ready, setReady] = createSignal(false)
  let refreshingHealth = false
  const connectionRevision = createMemo(() =>
    (fleet.machines() ?? []).map((machine) => [
      machine.name,
      machine.public_url,
      machine.local_url,
      JSON.stringify(machine.public_routes),
      machine.route_policy,
      machine.accessToken,
      machine.accessTokenExpiresAt,
      machine.accessError,
      machine.health?.state,
    ].join("\0")).join("\n"),
  )

  createEffect(() => {
    connectionRevision()
    const cachedMachines = untrack(() => fleet.machines()) as FleetMachine[] | undefined
    if (!fleetStartupReady(fleet.machines.loading, cachedMachines?.length ?? 0)) return
    if (fleet.machines.error) {
      setReady(true)
      return
    }
    const controller = new AbortController()
    void (async () => {
      try {
        const machines = untrack(() => fleet.machines()) as FleetMachine[] | undefined
        if (!machines || controller.signal.aborted) return
        if (machines.length === 0) {
          await purgeFleetWorkspaceState()
          if (controller.signal.aborted) return
          for (const existing of server.list) server.remove(ServerConnection.key(existing))
          server.clearActive()
          server.clearRememberedMachine()
          server.clearSessionRoutes()
          return
        }

        const urlMachine = parseMachineFromPath(location.pathname)
        const exists = (name: string | undefined): name is string =>
          !!name && machines.some((machine) => machine.name === name)
        const preferredName = urlMachine
          ? exists(urlMachine) ? urlMachine : undefined
          : chooseMachine(machines, [server.name, server.lastMachine])?.name
        // A failed attempt leaves the machine to its connection gate; it must not skip reconciliation.
        if (preferredName) await fleet.ensureMachineConnection(preferredName, controller.signal).catch(() => undefined)
        if (controller.signal.aborted) return

        const accessible = (untrack(() => fleet.machines()) ?? machines) as FleetMachine[]
        const addedKeys: ServerConnection.Key[] = []
        const byName = new Map<string, ServerConnection.Key>()
        // A lapsed grant keeps the transport: removing it would strand a shown machine outside its gate's retries.
        for (const machine of accessible) {
          const connection = fleet.installTransport(machine)
          if (!connection) continue
          const key = ServerConnection.key(connection)
          addedKeys.push(key)
          byName.set(machine.name, key)
        }
        const staleKeys = server.list
          .map((existing) => ServerConnection.key(existing))
          .filter((key) => !addedKeys.includes(key))

        const pick = () => {
          if (urlMachine) {
            const key = byName.get(urlMachine)
            return key ? { key, name: urlMachine } : undefined
          }
          const active = server.key
            ? accessible.find((machine) => machineKey(machine.public_url) === server.key)?.name
            : undefined
          const machine = chooseMachine(
            accessible.filter((candidate) => candidate.accessToken && byName.has(candidate.name)),
            [active, server.lastMachine],
          )
          return machine ? { key: byName.get(machine.name)!, name: machine.name } : undefined
        }

        const selected = pick()
        if (selected) server.setActive(selected.key, { machineName: selected.name })
        else if (urlMachine) server.clearActive()
        for (const key of staleKeys) server.remove(key)

        if (!refreshingHealth && accessible.some((machine) => machine.health?.state === "unknown")) {
          refreshingHealth = true
          void fleet.refetch().finally(() => { refreshingHealth = false })
        }
      } finally {
        if (!controller.signal.aborted) setReady(true)
      }
    })()
    onCleanup(() => controller.abort())
  })

  return (
    <Show
      when={ready() || server.key}
      fallback={<div class="h-dvh w-screen bg-background-base" role="status"><span class="sr-only">Loading your machines</span></div>}
    >
      {props.children}
    </Show>
  )
}
